import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { toEventBaseSlug } from 'lib/search/searchCalendarEvents'
import { useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'

const legendMessages = defineMessages({
  oktoberfest_munich: {
    id: 'mjrKvyAgpGTIABz57Gfl',
    defaultMessage: 'Oktoberfest ({startDate}—{endDate})',
  },
  diwali_india: {
    id: 'kph_D3LtzavUweJFoS9a',
    defaultMessage: 'Diwali ({startDate}—{endDate})',
  },
  sydney_nye_fireworks: {
    id: 'VFZJBhzOPPWSWmi2oZRE',
    defaultMessage: 'Sydney New Year’s Eve ({startDate}—{endDate})',
  },
  chinese_new_year_china: {
    id: 'Z2cFi99LzoNxuj0EHLrV',
    defaultMessage: 'Lunar New Year ({startDate}—{endDate})',
  },
  rio_carnival: {
    id: 'a0M1I5XMC39fWx_Qvghn',
    defaultMessage: 'Rio Carnival ({startDate}—{endDate})',
  },
  mardi_gras_new_orleans: {
    id: 'V7LXxYyfobUpilCcVo3A',
    defaultMessage: 'Mardi Gras ({startDate}—{endDate})',
  },
  holi_mathura: {
    id: 'sZ5kRaEPGbHDCn2bNgyt',
    defaultMessage: 'Holi ({startDate}—{endDate})',
  },
  cherry_blossom_japan: {
    id: 'SLPy8Uq5d-zVadh4kUqV',
    defaultMessage: 'Cherry blossom season ({startDate}—{endDate})',
  },
  songkran_thailand: {
    id: 'gXZOqyakHJK_txWofvvT',
    defaultMessage: 'Songkran ({startDate}—{endDate})',
  },
  san_fermin_pamplona: {
    id: 'BOwyj2kqRzjGDRKJ5tns',
    defaultMessage: 'San Fermín ({startDate}—{endDate})',
  },
})

const tooltipMessages = defineMessages({
  oktoberfest_munich: {
    id: 'W3VJVj0otoBkb5MxzO4A',
    defaultMessage: 'Munich’s beer festival, with tents, music and traditional Bavarian dress.',
  },
  diwali_india: {
    id: 'UKKcWoCCCLiF-GcaKGqy',
    defaultMessage: 'India’s festival of lights, marked by lamps, sweets and fireworks.',
  },
  sydney_nye_fireworks: {
    id: 'A1NVEiCNGXeMUzS8B0DE',
    defaultMessage: 'Sydney Harbour fireworks, one of the world’s most watched New Year’s Eve nights.',
  },
  chinese_new_year_china: {
    id: 'XyM-rlhC7vl_0K9gjsV5',
    defaultMessage: 'Lunar New Year celebrations across East and Southeast Asia, with family gatherings and lanterns.',
  },
  rio_carnival: {
    id: '1vI8J4bFUBisaJ0YQxmG',
    defaultMessage: 'Rio’s street carnival, with samba parades, music and elaborate costumes.',
  },
  mardi_gras_new_orleans: {
    id: 'V_jAuS1_cdoY5ALDZOog',
    defaultMessage: 'New Orleans’ carnival, with parades, masks and colourful beads.',
  },
  holi_mathura: {
    id: '3ktZl3U46JhYVmQlnvsG',
    defaultMessage: 'India’s festival of colour, where people throw powdered dyes to welcome spring.',
  },
  cherry_blossom_japan: {
    id: 'cfx6GxcCofa6W1ShU6MS',
    defaultMessage: 'Japan’s sakura season, when parks and temples fill with pale pink blooms.',
  },
  songkran_thailand: {
    id: 'atMMwAVYnR0JAioZYi6u',
    defaultMessage: 'Thailand’s New Year water festival, with street splashing and temple visits.',
  },
  san_fermin_pamplona: {
    id: 'y0-dQBFzw_E3ZDFSPnHa',
    defaultMessage: 'Pamplona’s festival, best known for the running of the bulls.',
  },
})

const fallbackMessages = defineMessages({
  label: {
    id: '_6ZQAJTGnNP2P_c0GJRy',
    defaultMessage: '{title} ({startDate}—{endDate})',
  },
  singleDayLabel: {
    id: 'urOgnrogVUkcLS8wioqA',
    defaultMessage: '{title} ({startDate})',
  },
})

type MessageKey = keyof typeof legendMessages

/** Feed ids carry the season year; the hand-written copy is per-event, not per-season. */
function messageKeyForEventId(eventId: string): MessageKey | undefined {
  const baseSlug = toEventBaseSlug(eventId)
  return baseSlug in legendMessages ? baseSlug as MessageKey : undefined
}

function formatLegendDate(isoDate: string, locale: string) {
  return intlDateFormatter(isoDate, INTL_DAY_MONTH_NAME_SHORT, locale)
}

/**
 * Legend labels and hover tooltips for calendar events, keyed on the full event id.
 *
 * Only a minority of the feed's events have hand-written copy above — at the time
 * of writing, 10 of its 50 base slugs. The rest resolve through `fallbackMessages`,
 * which localises the date wrapper but renders the feed's English `title` and
 * `shortDescription` in every locale. That is deliberate for now: the feed is the
 * copy source of truth and may grow its own translations. To translate one, add a
 * `legendMessages`/`tooltipMessages` pair keyed on its base slug (the id minus the
 * season year, e.g. `sapporo_snow_festival`) and it resolves with no other change.
 */
function useSearchCalendarEventNames(events: ReadonlyArray<SearchCalendarEvent>) {
  const intl = useIntl()

  return useMemo(() => {
    return Object.fromEntries(events.map(event => {
      const startDate = formatLegendDate(event.startDate, intl.locale)
      const endDate = formatLegendDate(event.endDate, intl.locale)
      const messageKey = messageKeyForEventId(event.id)
      const message = messageKey ? legendMessages[messageKey] : undefined
      const tooltip = messageKey ? tooltipMessages[messageKey] : undefined

      // Rows sourced from the events feed carry their own copy rather than a
      // hand-written message. The date wrapper stays localised either way; only
      // the title and description come through untranslated.
      const label = message ?
          intl.formatMessage(message, { startDate, endDate }) :
          intl.formatMessage(
            event.startDate === event.endDate ? fallbackMessages.singleDayLabel : fallbackMessages.label,
            { title: event.title, startDate, endDate },
          )

      return [event.id, {
        label,
        tooltip: tooltip ? intl.formatMessage(tooltip) : event.shortDescription ?? '',
      }]
    })) as Record<string, { label: string, tooltip: string } | undefined>
  }, [events, intl])
}

export default useSearchCalendarEventNames
